import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import styles from './Tabs.module.scss';
import { ReactNode, useEffect, useMemo } from 'react';
import { tabIndexState } from '../../../../state/App.state';

export interface ITab {
  hidden?: boolean;
  buttonLabel: string;
  component: ReactNode;
}

export interface ITabsComponentProps {
  tabs: ITab[];
  initialTab: number;
}

const { tabGroupContainer, tabList, tabContainer, tabHeader } = styles;

const Tabs: React.FunctionComponent<ITabsComponentProps> = ({ tabs, initialTab }) => {
  const tabIndex = useRecoilValue(tabIndexState);
  const setTabIndex = useSetRecoilState(tabIndexState);
  const resetTabIndex = useResetRecoilState(tabIndexState);

  useEffect(() => {
    resetTabIndex();
  }, [resetTabIndex]);

  const visibleTabs = useMemo(() => tabs.filter((t) => !t.hidden), [tabs]);

  useEffect(() => {
    // no tabs available
    if (visibleTabs.length === 0) {
      return;
    }

    // initial load
    if (tabIndex === -1) {
      const safeInitialIndex = initialTab >= 0 && initialTab < visibleTabs.length ? initialTab : 0;

      setTabIndex(safeInitialIndex);
      return;
    }

    // invalid index
    if (tabIndex >= visibleTabs.length) {
      setTabIndex(0);
    }
  }, [tabIndex, initialTab, visibleTabs, setTabIndex]);

  return (
    <div className={tabGroupContainer}>
      <div className={tabHeader}>
        <div className={tabList}>
          {visibleTabs.map((x, i) => (
            <button key={i} id={x.buttonLabel} role='tab' aria-selected={tabIndex === i} onClick={() => setTabIndex(i)}>
              <span>{x.buttonLabel}</span>
            </button>
          ))}
        </div>
      </div>

      <div className={tabContainer}>
        {visibleTabs.map((x, i) => (
          <div key={i} style={{ display: i === tabIndex ? 'block' : 'none' }}>
            {x.component}
          </div>
        ))}
      </div>
    </div>
  );
};

Tabs.displayName = 'Tabs';

export default Tabs;
